// Game Level Enum
export enum GameLevel {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate', 
  ADVANCED = 'advanced',
  OPEN = 'opened'
}

// Game Modality Enum
export enum GameModality {
  FOOTBALL = 'FOOTBALL',
  BASKETBALL = 'BASKETBALL',
  VOLLEYBALL = 'VOLLEYBALL',
  TENNIS = 'TENNIS',
  FUTSAL = 'FUTSAL',
  BEACH_TENNIS = 'BEACH_TENNIS',
  PADEL = 'PADEL',
  RUNNING = 'RUNNING'
}

// User Participation Status Enum
export enum ParticipationStatus {
  NOT_PARTICIPANT = 'NOT_PARTICIPANT', // Usuário não é participante do jogo
  INVITED = 'INVITED',           // Usuário foi convidado
  CONFIRMED = 'CONFIRMED',       // Usuário confirmou participação
  CANCELED = 'CANCELED',         // Usuário cancelou participação
  DECLINED = 'DECLINED'          // Usuário recusou convite
}

// User Role in Game Enum
export enum ParticipantRole {
  OWNER = 'OWNER',               // Dono do jogo
  ADMIN = 'ADMIN',               // Administrador do jogo
  PLAYER = 'PLAYER'              // Jogador comum
}

// Game Access Type Enum
export enum GameAccessType {
  PUBLIC = 'public',             // Jogo público
  LINK_ONLY = 'linkOnly',        // Apenas por link
  PRIVATE = 'private'            // Jogo privado
}

// Charge Type Enum
export enum ChargeType {
  DIVIDED = 'dividido',          // Valor dividido entre participantes
  FIXED = 'valor_fixo'           // Valor fixo por participante
}

// Participant interface
export interface Participant {
  id: string
  name: string
  nickname?: string
  userId?: string
  gameId: string
  status: ParticipationStatus
  role: ParticipantRole
  avatar?: string
}

// Game interface using enums
export interface Game {
  id: string
  name: string
  modality: GameModality
  startDate: Date
  endDate: Date
  location: string
  address: string
  price: number
  paymentMode: ChargeType
  occupiedSlots: number
  totalSlots: number
  level: GameLevel
  accessType: GameAccessType
  accessToken?: string
  userStatus: ParticipationStatus
  userRole: ParticipantRole
  participants?: Participant[]
}

// Helper functions to get UI information from enums
export const GameLevelLabels = {
  [GameLevel.BEGINNER]: 'Iniciante',
  [GameLevel.INTERMEDIATE]: 'Intermediário',
  [GameLevel.ADVANCED]: 'Avançado',
  [GameLevel.OPEN]: 'Aberto'
} as const

export const GameModalityLabels = {
  [GameModality.FOOTBALL]: 'Futebol',
  [GameModality.BASKETBALL]: 'Basquete',
  [GameModality.VOLLEYBALL]: 'Vôlei',
  [GameModality.TENNIS]: 'Tênis',
  [GameModality.FUTSAL]: 'Futsal',
  [GameModality.BEACH_TENNIS]: 'Beach Tennis',
  [GameModality.PADEL]: 'Padel',
  [GameModality.RUNNING]: 'Corrida'
} as const

// Helper functions
export function getGameLevelLabel(level: GameLevel): string {
  return GameLevelLabels[level]
}

export function getGameModalityLabel(modality: GameModality): string {
  return GameModalityLabels[modality]
}

// Get all available options for UI
export function getAllGameLevels(): Array<{ value: GameLevel; label: string }> {
  return Object.values(GameLevel).map(level => ({
    value: level,
    label: getGameLevelLabel(level)
  }))
}

export function getAllGameModalities(): Array<{ value: GameModality; label: string }> {
  return Object.values(GameModality).map(modality => ({
    value: modality,
    label: getGameModalityLabel(modality)
  }))
}

// Date formatting helpers
export function formatGameDate(date: Date): string {
  if (!date || isNaN(date.getTime())) return 'Invalid Date'
  return date.toLocaleDateString('pt-BR')
}

export function formatGameTime(date: Date): string {
  if (!date || isNaN(date.getTime())) return '--:--'
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

export function formatGameDateTime(date: Date): { date: string; time: string } {
  return {
    date: formatGameDate(date),
    time: formatGameTime(date)
  }
}

// Generate share URL for game based on access type
export function generateGameShareURL(game: Game, baseURL: string = window.location.origin): string {
  const gameDetailURL = `${baseURL}/app/games/${game.id}`
  
  if (game.accessType === GameAccessType.LINK_ONLY && game.accessToken) {
    return `${gameDetailURL}?token=${game.accessToken}`
  }
  
  return gameDetailURL
}

// Generate share text for game
export function generateGameShareText(game: Game): string {
  const dateFormatted = formatGameDate(game.startDate)
  const timeFormatted = formatGameTime(game.startDate)
  const modalityLabel = getGameModalityLabel(game.modality)
  const priceFormatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(game.price)

  let paymentText = ''
  if (game.paymentMode === ChargeType.DIVIDED) {
    const perPerson = game.price / game.totalSlots
    const perPersonFormatted = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(perPerson)
    paymentText = `${priceFormatted} total (${perPersonFormatted}/pessoa)`
  } else {
    paymentText = `${priceFormatted} por pessoa`
  }

  return `🏆 ${game.name}

🏃‍♂️ ${modalityLabel}
📅 ${dateFormatted} às ${timeFormatted}
📍 ${game.location}
💰 ${paymentText}
👥 ${game.totalSlots} vagas

Vem jogar comigo! 🎯`
}

// Game Creation Interface
export interface GameLocation {
  address: string
  name: string
  googlePlaceID?: string | null
  coordinate: {
    latitude: number
    longitude: number
  }
}

export interface GameCharge {
  type: ChargeType
  amount: number
}

export interface CreateGameData {
  startTime: string
  endTime: string
  accessType: GameAccessType
  modality: GameModality
  name: string
  level: GameLevel
  minParticipants: number
  maxParticipants: number
  charge: GameCharge
  location: GameLocation
}